import { formatDistanceToNow } from 'date-fns';
import type { CrmRequiredActionItem } from '../crm/requiredActions';
import { getRequiredActionTimestamp } from '../crm/requiredActions';

export interface CampaignBillingRequiredActionRow {
  id: string;
  name: string;
  status: string;
  billing_status?: string | null;
  billing_payment_intent_id?: string | null;
  recipient_count?: number | null;
  scheduled_at?: string | null;
  updated_at?: string | null;
  created_at: string;
  channel_type?: string | null;
}

/** Email campaigns that need retry charge and/or send after a billing or send failure. */
export function buildCampaignBillingRequiredActions(
  campaigns: CampaignBillingRequiredActionRow[],
  now: Date = new Date(),
): CrmRequiredActionItem[] {
  const items: CrmRequiredActionItem[] = [];

  for (const campaign of campaigns) {
    if ((campaign.channel_type || 'email') === 'sms') continue;

    const billingFailed = campaign.billing_status === 'failed';
    const statusFailed = campaign.status === 'failed';
    if (!billingFailed && !statusFailed) continue;

    const title = campaign.name?.trim() || 'Untitled campaign';
    const updatedAt = campaign.updated_at || campaign.scheduled_at || campaign.created_at;
    const updated = new Date(updatedAt);
    const updatedMs = updated.getTime();
    const recipientCount = campaign.recipient_count ?? 0;
    const amount = recipientCount * 0.01;
    const amountLabel = recipientCount > 0 ? ` · $${amount.toFixed(2)}` : '';
    const alreadyPaid =
      campaign.billing_status === 'captured' && !!campaign.billing_payment_intent_id;

    items.push({
      kind: 'campaign_billing_failed',
      campaignId: campaign.id,
      title,
      customerName: 'Email campaign',
      subtitle: Number.isNaN(updatedMs)
        ? alreadyPaid
          ? `Send failed after payment${amountLabel} · retry send`
          : `Payment failed${amountLabel} · retry charge & send`
        : alreadyPaid
          ? `Send failed ${formatDistanceToNow(updated, { addSuffix: true })}${amountLabel}`
          : `Payment failed ${formatDistanceToNow(updated, { addSuffix: true })}${amountLabel}`,
      sortOrder: Number.isNaN(updatedMs) ? now.getTime() : updatedMs,
      createdAt: updatedAt,
    });
  }

  items.sort((a, b) => getRequiredActionTimestamp(b) - getRequiredActionTimestamp(a));
  return items;
}
